import React from 'react';
import {Swiper, SwiperClass} from 'swiper/react';
import {
  Text,
  TextAlignment,
  TokenTextAppearance,
  CTA,
} from '@volkswagen-onehub/components-core';
import SpaceLike from '../../components/Space';
import styled from 'styled-components';
import Step from './step';
import {Steps} from '../app-screen';
import 'swiper/css/effect-fade';
import {config} from '../../environment';
import ArIcon from '../ar-icon';
import FadeInImage from '../fade-in-image';
import useDeviceType, {DeviceType} from '../../hooks/useDeviceType';
import {useSendAmpChargeAnalytic} from '../../hooks/useSendAmpChargeAnalytic';

const StyledText = styled(Text)`
  color: white;
`;

const ContinueButton = styled(CTA)`
  background-color: #fff;
  color: #001e50;
  border: none;
  width: 100%;

  & svg > path {
    fill: #001e50;
  }
`;

type Props = {
  setStep: (step: Steps) => void;
};

enum Charger {
  'Premium_7kw' = 0,
  /** These options are not available yet */
  // 'Premium_22kW' = 1,
  // 'Basic_7kw' = 2,
}

type ChargeAssestConfig = Record<
  Charger,
  {
    src?: string;
  }
>;

type ArFileOption = Exclude<DeviceType, 'Other'>;

const ChooseSwiper: React.FC<Props> = ({setStep}) => {
  const [activeIndex, setActiveIndex] = React.useState<Charger>(
    Charger.Premium_7kw,
  );
  const deviceType = useDeviceType();
  const {handleViewChargerDeviceInARClick} = useSendAmpChargeAnalytic();

  const ArFileMap: Record<ArFileOption, ChargeAssestConfig> = {
    iOS: {
      [Charger.Premium_7kw]: {
        src: `${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}www_20240820/RC_7kW.reality`,
      },
      /** These options are not available yet */
      // [Charger.Premium_22kW]: {
      //   src: `${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}www_20240820/RC_22kW.reality`,
      // },
      // [Charger.Basic_7kw]: {
      //   src: `${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}www_20240820/RC_7kW.reality`,
      // },
    },
    Android: {
      [Charger.Premium_7kw]: {
        src: `${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}www_20240820/android7k/index.html`,
      },
      /** These options are not available yet */
      // [Charger.Premium_22kW]: {
      //   src: `${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}www_20240820/android/index.html`,
      // },
      // [Charger.Basic_7kw]: {
      //   src: `${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}www_20240820/android7k/index.html`,
      // },
    },
  };

  const arFile = ArFileMap?.[deviceType as ArFileOption]?.[activeIndex];

  const chargerDetails = [
    {
      title: '7kW Premium',
      description:
        'This single phase charger is ideal for home and business use. It’s Australian made and works with all Volkswagen EVs. Multiple colours available.',
      image: `${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}7kw-premium.png`,
      customStyles: {width: '100%', marginTop: '-20%'},
    },
    /** These options are not available yet */
    // {
    //   title: '22kW Premium',
    //   description:
    //     'This charger is 50% larger than the 7kW charger and will provide up to 22kW of power if you have a three-phase connection. Multiple colours available.',
    //   image: `${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}22kw-premium.png`,
    //   customStyles: {width: '90%', marginTop: '-20%'},
    // },
    // {
    //   title: '7kW Basic',
    //   description:
    //     'Like the Premium version, this charger provides up to 7kW of power from a single-phase connection and is compatible with all Volkswagen EVs. Multiple colours available.',
    //   image: `${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}7kw-premium.png`,
    //   customStyles: {width: '100%', marginTop: '-20%'},
    // },
  ];

  return (
    <SpaceLike
      direction="vertical"
      customStyles={{
        textAlign: 'center',
        justifyContent: 'center',
        paddingBottom: 'calc(50px + env(safe-area-inset-bottom))',
      }}
    >
      <StyledText
        appearance={TokenTextAppearance.label200}
        textAlign={TextAlignment.center}
        bold
      >
        Choose your charger
      </StyledText>
      <Swiper
        spaceBetween={100}
        slidesPerView={1}
        pagination={{clickable: true, type: 'bullets'}}
        onSlideChange={(swiper: SwiperClass) =>
          setActiveIndex(swiper.activeIndex)
        }
        direction="horizontal"
        style={
          {
            'width': '100%',
            'height': '100%',
            '--swiper-pagination-bottom': '24px',
            '--swiper-pagination-color': '#001E50',
            '--swiper-pagination-bullet-inactive-color': '#001E50',
            '--swiper-pagination-bullet-inactive-opacity': '0.5',
            '--swiper-pagination-bullet-horizontal-gap': '8px',
          } as React.CSSProperties
        }
      >
        {chargerDetails.map((charger, index) => (
          <Step
            key={index}
            title={charger.title}
            description={charger.description}
            isActive={activeIndex === index}
          >
            <FadeInImage
              alt=""
              src={charger.image}
              customStyles={charger.customStyles}
              isActive={activeIndex === index}
            />
          </Step>
        ))}
      </Swiper>
      {deviceType === 'iOS' ? (
        <a
          style={{width: '100%'}}
          data-testid="ios-ar-button"
          rel="ar"
          href={arFile?.src || ''}
          onClick={() => {
            setStep(Steps.FINAL);
            handleViewChargerDeviceInARClick(chargerDetails[activeIndex].title);
          }}
        >
          <img
            style={{width: '100%'}}
            alt="View in AR"
            src={`${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}AR.png`}
          />
        </a>
      ) : deviceType === 'Android' ? (
        <ContinueButton
          data-testid="android-ar-button"
          onClick={() => {
            setStep(Steps.FINAL);
            handleViewChargerDeviceInARClick(chargerDetails[activeIndex].title);
            window.open(arFile?.src || '', '_blank');
          }}
          emphasis="secondary"
          tag="a"
          icon={<ArIcon style={{marginRight: '2px'}} />}
        >
          View in AR
        </ContinueButton>
      ) : null}
    </SpaceLike>
  );
};

export default ChooseSwiper;
